import CombatManager from '../CombatManager';
import { requestD20 as manualD20, requestDice as manualDice } from '../DiceProvider';
import { collectCreatureFaces } from './CreatureDice';

// Shared preflight: reuse the existing roll plan, not another damage engine.
// Structured creature calls run after full action validation. date_of_change: 2026-09-05
const requireManualFaces = (rolls, count, dieSize, label) => {
  if (!Array.isArray(rolls) || rolls.length === 0) {
    const error = new Error(`${label} cancelled; no action was spent.`);
    error.code = 'ACTION_CANCELLED';
    throw error;
  }
  const normalized = rolls.map(Number);
  if (
    normalized.length !== count
    || normalized.some((roll) => !Number.isInteger(roll) || roll < 1 || roll > dieSize)
  ) {
    const error = new Error(`${label} requires ${count} valid d${dieSize} result${count === 1 ? '' : 's'}.`);
    error.code = 'MANUAL_ROLL_INVALID';
    throw error;
  }
  return normalized;
};

/**
 * Pre-collect the attack d20 and (if it hits) the weapon damage dice for a single-target
 * manual-roll attack, using the same async DiceProvider bridge the rest of the app already
 * binds to an in-app roll UI (no native window.prompt).
 * date_of_change: 2026-07-12
 * rule_reference: AGENTS.md (GM hooks; deterministic engine; no silent auto-fixes)
 *
 * Returns { manualRolls, manualRollQueue } ready to hand to CombatManager.executeAttack via
 * actionDataOverrides.__manualRolls / __manualRollQueue, or null when manual rolls aren't
 * needed/applicable (multi-target, no manual-roll mode, or no resolvable target/action).
 *
 * Scope note: this covers the primary attack roll, the primary weapon damage roll, Bless/Bane
 * bonus dice, and Sneak Attack's bonus dice. All of these are knowable ahead of the actual die
 * result: eligibility for Sneak Attack (weapon type, finesse/ranged, advantage/adjacency,
 * feature present, not already used this turn) comes entirely from previewAttackConditions'
 * structural derivation — the same one executeAttack itself uses — not from the roll. The only
 * thing gated by the roll, for both weapon damage and Sneak Attack damage alike, is whether the
 * attack hits at all, which is why both are collected only after attackRollResult.isHit is known.
 * date_of_change: 2026-07-12
 */
export async function collectManualAttackRolls(attacker, target, actionData, combatState) {
  if (!CombatManager.shouldUseManualRolls(attacker) && !combatState.creatureResolution) return null;
  const requestD20 = options => combatState.creatureResolution
    ? collectCreatureFaces(attacker, combatState, 20, options.numDice, () => (combatState.requestD20 || manualD20)(options))
    : (combatState.requestD20 || manualD20)(options);
  const requestDice = options => combatState.creatureResolution
    ? collectCreatureFaces(attacker, combatState, options.dieSize, options.numDice, () => manualDice(options))
    : manualDice(options);
  if (!target || !actionData) return null;

  const preview = CombatManager.previewAttackConditions(attacker, target, actionData, combatState, {});
  if (preview.validation?.valid === false) return null;
  const resolvedAction = preview.resolvedAction || actionData;
  const advantage = !!preview.advantage;
  const disadvantage = !!preview.disadvantage;
  const d20Count = advantage !== disadvantage ? 2 : 1;
  const attackRules = CombatManager.resolveAttackRules(combatState);

  const attackRolls = requireManualFaces(await requestD20({
    numDice: d20Count,
    title: `${resolvedAction?.name || actionData.name || 'Attack'} attack roll`,
    prompt: d20Count > 1
      ? `Enter both d20 faces (${advantage ? 'advantage' : 'disadvantage'}) — VCS keeps the ${advantage ? 'higher' : 'lower'} and adds modifiers automatically.`
      : 'Enter the raw d20 face. VCS adds modifiers automatically.'
  }), d20Count, 20, 'Attack roll');

  // Bless/Bane bonus dice on the attack roll are knowable ahead of time from status effects
  // (no randomness needed to know IF they apply — only what face comes up).
  const statusEffects = attacker?.statusEffects || {};
  let blessValue = null;
  let baneValue = null;
  if (statusEffects.bless) {
    [blessValue] = requireManualFaces(
      await requestDice({ numDice: 1, dieSize: 4, title: 'Bless attack modifier', prompt: 'Enter the raw d4 face for Bless.' }),
      1,
      4,
      'Bless roll'
    );
  }
  if (statusEffects.bane) {
    [baneValue] = requireManualFaces(
      await requestDice({ numDice: 1, dieSize: 4, title: 'Bane attack modifier', prompt: 'Enter the raw d4 face for Bane.' }),
      1,
      4,
      'Bane roll'
    );
  }
  const blessBaneValues = [blessValue, baneValue].filter((v) => v != null);

  // Determine hit/crit deterministically by replaying the same pure calculation the engine
  // will run for real, fed with the exact values the player just entered (fresh array copies
  // so this preview call doesn't consume the queue we still need for the real execution below).
  const attackRollResult = CombatManager.calculateAttackRoll(attacker, target, resolvedAction, {
    advantage,
    disadvantage,
    manualRolls: attackRolls,
    manualRollQueue: [...blessBaneValues],
    attackRules
  });

  const damageValues = [];
  let hasWeaponDamage = false;
  if (attackRollResult?.isHit) {
    const plan = CombatManager.getDamageRollPlan(resolvedAction, !!attackRollResult.isCritical, {
      attacker,
      criticalDiceMultiplier: attackRules.criticalHit.damageDiceMultiplier
    });
    hasWeaponDamage = plan.length > 0;
    for (const step of plan) {
      const label = step.phase === 'critical'
        ? `${resolvedAction?.name || 'Attack'} critical damage (${step.type})`
        : `${resolvedAction?.name || 'Attack'} damage (${step.type})`;
      // eslint-disable-next-line no-await-in-loop
      const rolls = requireManualFaces(await requestDice({
        numDice: step.diceCount,
        dieSize: step.diceSize,
        title: label,
        prompt: `Enter ${step.diceCount} raw d${step.diceSize} face${step.diceCount > 1 ? 's' : ''}. VCS adds any flat modifier automatically.`
      }), step.diceCount, step.diceSize, label);
      damageValues.push(...rolls);
    }

    // Sneak Attack: eligibility is fully determined by previewAttackConditions (weapon type,
    // finesse/ranged, advantage/adjacency, feature present, not used this turn already) — none
    // of that depends on the attack roll's face, only on whether the attack hits at all, same as
    // weapon damage above. Matches executeAttack's own gate: hasSAFeature && canApplySA &&
    // !alreadyUsed && result.damage (hasWeaponDamage stands in for result.damage here).
    if (hasWeaponDamage && preview.sneakAttack?.eligible) {
      const sneakDamageDice = preview.sneakAttack.damageDice;
      if (!sneakDamageDice) {
        return {
          manualRolls: attackRolls,
          manualRollQueue: [...blessBaneValues, ...damageValues]
        };
      }
      const saPlan = CombatManager.getDamageRollPlan(
        { name: 'Sneak Attack', damage: [{ dice: sneakDamageDice }] },
        !!attackRollResult.isCritical,
        { attacker, criticalDiceMultiplier: attackRules.criticalHit.damageDiceMultiplier }
      );
      for (const step of saPlan) {
        const label = step.phase === 'critical'
          ? 'Sneak Attack critical damage'
          : 'Sneak Attack damage';
        // eslint-disable-next-line no-await-in-loop
        const rolls = requireManualFaces(await requestDice({
          numDice: step.diceCount,
          dieSize: step.diceSize,
          title: label,
          prompt: `Enter ${step.diceCount} raw d${step.diceSize} face${step.diceCount > 1 ? 's' : ''} for Sneak Attack.`
        }), step.diceCount, step.diceSize, label);
        damageValues.push(...rolls);
      }
    }
  }

  return {
    manualRolls: attackRolls,
    manualRollQueue: [...blessBaneValues, ...damageValues]
  };
}

